import { Clock, LogOut, ShieldCheck, UserRound } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RoleBadges } from '@/components/common/RoleBadges'
import { env } from '@/config/env'
import { useLogout } from '@/features/auth/hooks/use-auth'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type { UserStatus } from '@/features/auth/types'
import { formatRelativeTime, initials } from '@/lib/format'

/** Active is the unremarkable case and says nothing; the other two are worth showing. */
const STATUS_LABEL: Partial<Record<UserStatus, string>> = {
  Invited: 'Invitation not yet used',
  Disabled: 'Access turned off',
}

export function UserMenu() {
  const user = useAuthStore((state) => state.user)
  const logout = useLogout()

  if (!user) return null

  const status = STATUS_LABEL[user.status]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 gap-2 px-2">
          <Avatar className="size-7">
            {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
            <AvatarFallback>{initials(user.name)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-32 truncate text-body sm:inline">{user.name}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-3 py-2">
          <Avatar className="size-9">
            {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
            <AvatarFallback>{initials(user.name)}</AvatarFallback>
          </Avatar>
          <span className="flex min-w-0 flex-col">
            <span className="truncate">{user.name}</span>
            <span className="text-muted-foreground truncate text-meta font-normal">
              {user.email}
            </span>
          </span>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Plain rows, not menu items: these are facts about the account, nothing to click. */}
        <div className="text-muted-foreground space-y-2 px-2 py-1.5 text-meta">
          <div className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <RoleBadges roles={user.roles} />
          </div>

          {user.personName ? (
            <div className="flex items-center gap-2">
              <UserRound className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">On the roster as {user.personName}</span>
            </div>
          ) : null}

          {user.lastSignedInAt ? (
            <div className="flex items-center gap-2">
              <Clock className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">Signed in {formatRelativeTime(user.lastSignedInAt)}</span>
            </div>
          ) : null}

          {status ? <div className="text-destructive">{status}</div> : null}
        </div>

        {env.requireAuth ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={logout.isPending}
              onSelect={() => logout.mutate()}
            >
              <LogOut className="size-4" aria-hidden />
              Sign out
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
